import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import API from '@/lib/api';
import { Printer, CheckCircle, XCircle, Trash, ArrowsClockwise, Star } from '@phosphor-icons/react';

/* ════════════════════════════════════════════════════════════════════
   PrintNodePanel — Instellingen → Integraties
   API key invoeren + testen, printers bekijken, default kiezen.
   ════════════════════════════════════════════════════════════════════ */
export default function PrintNodePanel() {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [printersLoading, setPrintersLoading] = useState(false);

  const load = async () => {
    try {
      const r = await API.get('/printnode/config');
      setCfg(r.data);
      setEnabled(r.data.enabled !== false);
      setApiKey(r.data.api_key_masked || '');
      if (r.data.configured && r.data.enabled !== false) loadPrinters();
    } catch { /* stil */ }
  };

  const loadPrinters = async () => {
    setPrintersLoading(true);
    try {
      const r = await API.get('/printnode/printers');
      setPrinters(r.data?.printers || []);
    } catch (e) {
      setPrinters([]);
    } finally {
      setPrintersLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const testKey = async () => {
    if (!apiKey.trim()) { toast.error('Vul eerst een API key in'); return; }
    setBusy(true);
    setTestResult(null);
    try {
      const r = await API.post('/printnode/test', { api_key: apiKey.trim() });
      setTestResult(r.data);
      toast.success(`✅ Verbonden als ${r.data.account?.email} — ${r.data.printer_count} printer(s) gevonden`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setTestResult({ ok: false, error: msg });
      toast.error('Test mislukt: ' + msg);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!apiKey.trim()) { toast.error('Vul eerst een API key in'); return; }
    setBusy(true);
    try {
      await API.put('/printnode/config', { api_key: apiKey.trim(), enabled });
      toast.success('PrintNode opgeslagen en gevalideerd');
      await load();
    } catch (e) {
      toast.error('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('PrintNode-koppeling verwijderen?')) return;
    await API.delete('/printnode/config');
    setCfg(null); setApiKey(''); setPrinters([]); setTestResult(null);
    toast.success('PrintNode-koppeling verwijderd');
    load();
  };

  const setDefault = async (printerId) => {
    try {
      await API.put('/printnode/config', { api_key: apiKey || '••••', enabled, default_printer_id: printerId });
      toast.success('Standaardprinter ingesteld');
      loadPrinters();
    } catch (e) {
      toast.error('Mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4" data-testid="printnode-panel">
      <p className="text-xs text-gray-500">
        <a href="https://www.printnode.com" target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-semibold">PrintNode</a> is
        een cloud-printdienst: installeer de gratis PrintNode Client op de PC waar uw printers aan hangen,
        maak een API key aan op <a href="https://app.printnode.com/app/apikeys" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">app.printnode.com → API Keys</a> en
        plak hem hieronder. Daarna kan u vanaf elke locatie (ook mobiel) rechtstreeks afdrukken op uw kantoorprinters.
      </p>

      {cfg?.configured && (
        <div className="flex items-center gap-2 text-xs bg-green-50 border border-green-200 px-3 py-2" data-testid="printnode-status">
          <CheckCircle size={16} weight="fill" className="text-green-600" />
          <span className="font-semibold text-green-800">
            Verbonden{cfg.account?.email ? ` — ${cfg.account.email}` : ''}
          </span>
          <span className="ml-auto text-gray-400">{cfg.enabled !== false ? 'Actief' : 'Uitgeschakeld'}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          className="input flex-1 font-mono text-xs"
          placeholder="PrintNode API key (bv. xw8c_...)"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          data-testid="printnode-apikey-input"
        />
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm border border-gray-300" onClick={testKey} disabled={busy} data-testid="printnode-test-btn">
            {busy ? '…' : 'Test verbinding'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={busy} data-testid="printnode-save-btn">
            Opslaan
          </button>
          {cfg?.configured && (
            <button className="btn btn-ghost btn-sm text-red-600" onClick={remove} title="Koppeling verwijderen" data-testid="printnode-delete-btn">
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>

      {testResult && !testResult.ok && (
        <div className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 px-3 py-2 text-red-700" data-testid="printnode-test-error">
          <XCircle size={14} weight="fill" /> {testResult.error}
        </div>
      )}
      {testResult?.ok && (
        <div className="flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 px-3 py-2 text-blue-800" data-testid="printnode-test-ok">
          <CheckCircle size={14} weight="fill" className="text-green-600" />
          Account: <strong>{testResult.account?.email}</strong> · {testResult.printer_count} printer(s)
        </div>
      )}

      {cfg?.configured && (
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); API.put('/printnode/config', { api_key: '••••', enabled: e.target.checked }).then(() => toast.success(e.target.checked ? 'PrintNode geactiveerd' : 'PrintNode uitgeschakeld')).catch(() => {}); }} data-testid="printnode-enabled-toggle" />
          <span className="font-semibold">PrintNode actief (printers tonen in het print-menu bij documenten)</span>
        </label>
      )}

      {cfg?.configured && (
        <div className="border border-gray-200">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
              <Printer size={13} /> Printers in uw PrintNode-account
            </span>
            <button className="btn btn-ghost btn-sm" onClick={loadPrinters} title="Vernieuwen" data-testid="printnode-refresh-printers">
              <ArrowsClockwise size={13} className={printersLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          {printersLoading && <div className="px-3 py-3 text-xs text-gray-400">Laden…</div>}
          {!printersLoading && printers.length === 0 && (
            <div className="px-3 py-3 text-xs text-gray-500">
              Geen printers gevonden. Controleer of de <strong>PrintNode Client</strong> draait op de PC met uw printers.
            </div>
          )}
          {!printersLoading && printers.map(p => (
            <div key={p.id} className="px-3 py-2 border-b border-gray-100 last:border-b-0 flex items-center gap-2 text-xs" data-testid={`printnode-printer-${p.id}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${p.online ? 'bg-green-500' : 'bg-gray-400'}`} title={p.online ? 'Online' : 'Offline'}></span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{p.naam} {p.is_default && <span className="ml-1 text-[9px] uppercase px-1 py-0.5 bg-blue-100 text-blue-800">standaard</span>}</div>
                <div className="text-[10px] text-gray-400 truncate">{p.computer}{p.beschrijving ? ` · ${p.beschrijving}` : ''} · #{p.id}</div>
              </div>
              {!p.is_default && (
                <button className="btn btn-ghost btn-sm" onClick={() => setDefault(p.id)} title="Als standaardprinter instellen" data-testid={`printnode-set-default-${p.id}`}>
                  <Star size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
